import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  IRowNode,
  RowClassParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { EditEmbryoDialog } from '@/pages/embryo-app/components/EditEmbryoDialog';
import { useAccessToken } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import type { EmbryoDetailDto } from '@/services/embryo-service';
import {
  collectVersions,
  isNonTogEmbryoActionable,
  type OnFarmImplantSession,
  type OnFarmImplantSessionEmbryo,
  onFarmImplantSessionEmbryosOptions,
  useRemoveOnFarmImplantNonTogEmbryos,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import { AddToQueueDialog } from '../FrozenTab/AddToQueueDialog';
import { AddNonTogEmbryosDialog } from './AddNonTogEmbryosDialog';
import { useNonTogEmbryoColumnDefs } from './columnDefs';

type NonTogTabProps = {
  sessionId: string | null;
  session?: OnFarmImplantSession;
  readOnly?: boolean;
};

export const NonTogTab = ({
  sessionId,
  session,
  readOnly = false,
}: NonTogTabProps) => {
  const { t } = useTranslation();
  const token = useAccessToken();
  const confirm = useConfirm();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'sessionEmbryoId' });
  const gridRef = useRef<AgGridReact>(null);

  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);

  const { mutateAsync: removeEmbryos, isPending: isRemoving } =
    useRemoveOnFarmImplantNonTogEmbryos();

  const { data, isLoading, isError, refetch } = useQuery({
    ...onFarmImplantSessionEmbryosOptions(token, {
      sessionId: sessionId ?? '',
      sourceType: 'non-tog',
    }),
    enabled: !!token && !!sessionId,
  });

  const columnDefs = useNonTogEmbryoColumnDefs(readOnly);

  const rows = useMemo(() => data?.items ?? [], [data]);

  const canAdd = !readOnly && session?.status === 'Active';

  const selectableCount = useMemo(
    () => rows.filter(isNonTogEmbryoActionable).length,
    [rows],
  );

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedIds(
      (event.api.getSelectedRows() as OnFarmImplantSessionEmbryo[]).map(
        (row) => row.sessionEmbryoId,
      ),
    );
  }, []);

  const clearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
    setSelectedIds([]);
  }, []);

  const selectedEmbryos = useMemo(() => {
    const ids = new Set(selectedIds);
    return rows.filter((row) => ids.has(row.sessionEmbryoId));
  }, [rows, selectedIds]);

  const editableEmbryos = useMemo<EmbryoDetailDto[]>(
    () =>
      selectedEmbryos.map((embryo) => ({
        id: embryo.embryoId,
        name: embryo.embryoName ?? undefined,
        donorName: embryo.donorName ?? undefined,
        sireName: embryo.sireName ?? undefined,
        status: embryo.embryoStatus ?? undefined,
      })),
    [selectedEmbryos],
  );

  const handleEdited = useCallback(() => {
    clearSelection();
    void refetch();
  }, [clearSelection, refetch]);

  const isRowSelectable = useCallback(
    (node: IRowNode) =>
      !readOnly &&
      !!node.data &&
      isNonTogEmbryoActionable(node.data as OnFarmImplantSessionEmbryo),
    [readOnly],
  );

  const getRowClass = useCallback(
    (params: RowClassParams) =>
      params.data &&
      !isNonTogEmbryoActionable(params.data as OnFarmImplantSessionEmbryo)
        ? 'opacity-60'
        : undefined,
    [],
  );

  const handleRemove = useCallback(() => {
    if (!sessionId || selectedEmbryos.length === 0) return;
    const sessionEmbryoIds = selectedEmbryos.map(
      (embryo) => embryo.sessionEmbryoId,
    );
    confirm({
      title: t('onFarmImplant.nonTog.removeTitle'),
      description: t('onFarmImplant.nonTog.removeBody', {
        count: sessionEmbryoIds.length,
      }),
      confirmLabel: t('onFarmImplant.nonTog.remove'),
      confirmVariant: 'destructive',
      onConfirm: () => {
        removeEmbryos({
          sessionId,
          sessionEmbryoIds,
          versions: collectVersions(selectedEmbryos),
        })
          .then(() => {
            clearSelection();
            toast.add({
              data: { variant: 'success' },
              title: t('onFarmImplant.nonTog.removeSuccess', {
                count: sessionEmbryoIds.length,
              }),
            });
          })
          .catch(() => {
            toast.add({
              data: { variant: 'error' },
              title: t('onFarmImplant.nonTog.removeFailed'),
            });
          });
      },
    });
  }, [clearSelection, confirm, removeEmbryos, selectedEmbryos, sessionId, t]);

  if (!sessionId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-box mb-3 text-4xl" />
        <p className="text-sm">{t('onFarmImplant.nonTog.noSession')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.nonTog.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.nonTog.loadError')}</p>
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          {t('onFarmImplant.frozen.retry')}
        </Button>
      </div>
    );
  }

  if (!data?.sourceSelected) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-box mb-3 text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.nonTog.notLoadedTitle')}
        </p>
        <p className="mt-1 text-sm">
          {t('onFarmImplant.nonTog.notLoadedBody')}
        </p>
      </div>
    );
  }

  const addButton = (
    <Button size="sm" disabled={!canAdd} onClick={() => setAddDialogOpen(true)}>
      <i className="pi pi-plus" />
      {t('onFarmImplant.nonTog.add')}
    </Button>
  );

  const addDialog = session ? (
    <AddNonTogEmbryosDialog
      open={addDialogOpen}
      onOpenChange={setAddDialogOpen}
      session={session}
    />
  ) : null;

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-400">
        <i className="pi pi-inbox text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.nonTog.emptyTitle')}
        </p>
        <p className="text-sm">{t('onFarmImplant.nonTog.emptyBody')}</p>
        {addButton}
        {addDialog}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-56">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t('onFarmImplant.frozen.filterPlaceholder')}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.frozen.embryoCount', {
              count: data.totalRecords,
            })}
          </span>
          {addButton}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.frozen.selectedCount', {
              selected: selectedIds.length,
              total: selectableCount,
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={selectedIds.length === 0}
            onClick={clearSelection}
          >
            {t('onFarmImplant.frozen.clearSelection')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly || selectedIds.length === 0}
            onClick={() => setEditDialogOpen(true)}
          >
            {t('onFarmImplant.nonTog.edit')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={readOnly || selectedIds.length === 0 || isRemoving}
            onClick={handleRemove}
          >
            {t('onFarmImplant.nonTog.remove')}
          </Button>
          <Button
            size="sm"
            disabled={readOnly || selectedIds.length === 0}
            onClick={() => setQueueDialogOpen(true)}
          >
            {t('onFarmImplant.frozen.queue.addToQueue')}
          </Button>
        </div>
      </div>

      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-13rem)] min-h-[400px] w-full`}
      >
        <WithLocalStorage storageKey="on-farm-implant-non-tog-grid">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            defaultColDef={defaultColDef}
            rowData={rows}
            headerHeight={36}
            rowHeight={48}
            rowSelection="multiple"
            suppressRowClickSelection
            isRowSelectable={isRowSelectable}
            getRowClass={getRowClass}
            quickFilterText={searchText}
            getRowId={getRowId<OnFarmImplantSessionEmbryo>(
              (params) => params.data.sessionEmbryoId,
            )}
            getContextMenuItems={getContextMenuItems}
            onSelectionChanged={handleSelectionChanged}
          />
        </WithLocalStorage>
      </div>

      {addDialog}

      <EditEmbryoDialog
        visible={editDialogOpen}
        selectedEmbryos={editableEmbryos}
        onHide={() => setEditDialogOpen(false)}
        onSuccess={handleEdited}
      />

      <AddToQueueDialog
        open={queueDialogOpen}
        onOpenChange={setQueueDialogOpen}
        sessionId={sessionId}
        embryos={selectedEmbryos}
        onQueued={clearSelection}
        sourceType="non-tog"
      />
    </div>
  );
};
